import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import moment from "moment";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DefaultData from "../../CompanyData/DefaultData";
import {
  RiCheckboxCircleLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiArrowLeftRightLine,
  RiArrowLeftLine,
  RiErrorWarningLine,
  RiFileList3Line,
  RiPrinterLine,
  RiDownloadLine,
  RiShieldCheckLine,
  RiInformationLine,
  RiTicketLine,
  RiTimeLine,
  RiMoneyDollarCircleLine,
  RiBillLine,
} from "react-icons/ri";

const BusAgentInvoice = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengerData, setPassengerData] = useState([]);
  const [seatData, setSeatData] = useState([]);
  const [boardingPointDisplay, setBoardingPointDisplay] = useState("");
  const [droppingPointDisplay, setDroppingPointDisplay] = useState("");
  const [boardingTime, setBoardingTime] = useState("");
  const [droppingTime, setDroppingTime] = useState("");

  const contentRef = useRef(null);
  const printRef = useRef(null);

  // Get company info from DefaultData
  const companyInfo = {
    name: DefaultData.DEFAULTCOMPANYNAME || "SAAR DEVELOPERS CODE PRIVATE LIMITED",
    email: DefaultData.DEFAULTCOMPANYEMAIL || "support@zotrai.com",
    phone: DefaultData.DEFAULTCOMPANYPHONENUMBER || "9177656344",
    gst: DefaultData.DEFAULTCOMPANYGST || "36ANVPM2246A1Z0",
    address: DefaultData.DEFAULTCOMPANYADDRESS || "HYDERABAD, TELANGANA",
    brand: DefaultData.DEFAULTBRANDNAME || "ZOTRAI",
    website: DefaultData.WEBSITE || "https://trawtel.com",
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.replace(/:00(?=\s|$)/g, "");
  };

  const parseLocationData = (locationString) => {
    if (!locationString) return "";

    try {
      if (typeof locationString === 'string' && !locationString.startsWith('{')) {
        return locationString;
      }

      const parsed = JSON.parse(locationString);

      if (parsed.Location) {
        return parsed.Location;
      }
      return locationString;
    } catch (e) {
      return locationString;
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(
        `${config.GETBUSBOOKINGBYID}/${bookingId}`
      );

      if (response) {
        setBooking(response);

        if (response.boardingPoint) {
          setBoardingPointDisplay(parseLocationData(response.boardingPoint));
          try {
            const parsed = JSON.parse(response.boardingPoint);
            if (parsed.Time) {
              setBoardingTime(formatTime(parsed.Time));
            }
          } catch (e) {
            console.error("Error parsing boarding time:", e);
          }
        }

        if (response.droppingPoint) {
          setDroppingPointDisplay(parseLocationData(response.droppingPoint));
          try {
            const parsed = JSON.parse(response.droppingPoint);
            if (parsed.Time) {
              setDroppingTime(formatTime(parsed.Time));
            }
          } catch (e) {
            console.error("Error parsing dropping time:", e);
          }
        }

        if (response.passengerDetails) {
          try {
            const parsedPassengers = JSON.parse(response.passengerDetails);
            setPassengerData(
              Array.isArray(parsedPassengers)
                ? parsedPassengers
                : [parsedPassengers]
            );
          } catch (e) {
            console.error("Error parsing passenger details:", e);
            setPassengerData([]);
          }
        }

        if (response.seatDetails) {
          try {
            const parsedSeats = JSON.parse(response.seatDetails);
            setSeatData(
              Array.isArray(parsedSeats) ? parsedSeats : [parsedSeats]
            );
          } catch (e) {
            console.error("Error parsing seat details:", e);
            setSeatData([]);
          }
        }
      } else {
        setError("Booking details not found");
      }
    } catch (err) {
      setError("Failed to fetch booking details. Please try again.");
      console.error("Fetch booking error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
  navigate(-1);
};
  const formatDate = (dateString) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const formatCurrency = (amount) => {
    return `INR ${amount}`;
  };


  // Get payment summary for display
  const getPaymentSummary = () => {
    const baseFare = booking?.baseFare || booking?.totalFare || 0;
    const totalPrice = booking?.totalPrice || 0;
    const platformFee = Math.round(totalPrice - baseFare - Math.round(booking?.convenienceFee || 0) - Math.round(booking?.markup || 0) - Math.round(booking?.paymentGatewayAmount || 0) + Math.round(booking?.discount || 0));
    const convenienceFee = Math.round(booking?.convenienceFee || 0) + Math.round(booking?.markup || 0);
    const paymentGatewayAmount = Math.round(booking?.paymentGatewayAmount|| 0);
    const discount = Math.round(booking?.discount || 0);
    const totalAmount = Math.round(booking?.totalPrice || 0) - convenienceFee;
  
    return {
      baseFare,
      platformFee,
      convenienceFee,
      paymentGatewayAmount,
      discount,
      totalAmount
    };
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Agent Invoice - ${booking?.invoiceNo || booking?.bookingNo || 'Invoice'}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: A4;
              margin: 10mm;
            }
            
            body {
              font-family: Arial, Helvetica, sans-serif;
              background: #ffffff;
              padding: 20px;
              color: #000000;
            }
            
            .print-container {
              max-width: 1000px;
              margin: 0 auto;
            }
            
            .voucher-header {
              border-bottom: 2px solid #000000;
              padding-bottom: 20px;
              margin-bottom: 20px;
            }
            
            .voucher-title {
              font-size: 24px;
              font-weight: bold;
            }
            
            .voucher-subtitle {
              font-size: 14px;
              color: #666666;
            }
            
            .voucher-number {
              font-size: 18px;
              font-weight: bold;
              text-align: right;
            }
            
            .voucher-label {
              font-size: 12px;
              color: #666666;
              display: block;
            }
            
            .card {
              border: 1px solid #000000;
              border-radius: 4px;
              background: #ffffff;
              overflow: hidden;
              margin-bottom: 20px;
            }
            
            .card-body {
              padding: 20px;
            }
            
            .section-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 12px;
              border-bottom: 2px solid #000000;
              padding-bottom: 8px;
            }
            
            .row {
              display: flex;
              flex-wrap: wrap;
              margin: 0 -10px;
            }
            
            .col-4, .col-6, .col-8, .col-12 {
              padding: 0 10px;
            }
            
            .col-4 { flex: 0 0 33.333333%; max-width: 33.333333%; }
            .col-6 { flex: 0 0 50%; max-width: 50%; }
            .col-8 { flex: 0 0 66.666667%; max-width: 66.666667%; }
            .col-12 { flex: 0 0 100%; max-width: 100%; }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-muted { color: #666666; }
            .fw-bold { font-weight: bold; }
            .small { font-size: 12px; }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mb-3 { margin-bottom: 12px; }
            .mb-4 { margin-bottom: 20px; }
            .mt-4 { margin-top: 20px; }
            
            .border-left {
              border-left: 3px solid #000000;
              padding-left: 15px;
            }
            
            .border-top {
              border-top: 1px solid #000000;
              padding-top: 15px;
              margin-top: 15px;
            }
            
            .bg-light {
              background-color: #f8f9fa;
              padding: 15px;
              border-radius: 4px;
              border: 1px solid #000000;
            }
            
            .table {
              width: 100%;
              border-collapse: collapse;
            }
            
            .table th,
            .table td {
              padding: 10px 12px;
              border: 1px solid #000000;
              text-align: left;
            }
            
            .table-light {
              background-color: #f8f9fa;
            }
            
            .table-light th {
              background-color: #f8f9fa;
            }
            
            hr {
              border: none;
              border-top: 1px solid #000000;
              margin: 15px 0;
            }
            
            .list-unstyled {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            
            .list-unstyled li {
              padding: 4px 0;
            }
            
            .d-flex {
              display: flex;
            }
            
            .justify-content-between {
              justify-content: space-between;
            }
            
            .align-items-center {
              align-items: center;
            }
            
            .gap-2 { gap: 8px; }
            .gap-3 { gap: 16px; }
            
            .me-1 { margin-right: 4px; }
            .me-2 { margin-right: 8px; }
            
            .rounded-circle {
              border-radius: 50%;
              border: 2px solid #000000;
              padding: 10px;
              display: inline-block;
            }
            
            .fs-2 { font-size: 24px; }
            .fs-4 { font-size: 20px; }
            
            .d-inline-block {
              display: inline-block;
            }
            
            .px-3 {
              padding-left: 16px;
              padding-right: 16px;
            }
            
            .py-1 {
              padding-top: 4px;
              padding-bottom: 4px;
            }
            
            .rounded-pill {
              border-radius: 50px;
              border: 1px solid #000000;
              padding: 4px 16px;
              display: inline-block;
              font-size: 12px;
            }

            .payment-summary {
              border: 1px solid #000000;
              padding: 15px;
              margin-top: 15px;
            }

            .payment-summary .row {
              padding: 6px 0;
            }

            .payment-summary .total-row {
              border-top: 2px solid #000000;
              padding-top: 10px;
              margin-top: 6px;
              font-weight: bold;
              font-size: 16px;
            }

            .gst-text {
              font-size: 11px;
              color: #666666;
              padding-left: 5px;
            }

            .alert-success {
              background-color: #d4edda;
              border: 1px solid #c3e6cb;
              color: #155724;
              padding: 10px;
              border-radius: 4px;
              margin-top: 10px;
            }

            .alert-warning {
              background-color: #fff3cd;
              border: 1px solid #ffeeba;
              color: #856404;
              padding: 10px;
              border-radius: 4px;
              margin-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent.outerHTML}
          </div>
          <script>
            window.onload = function() {
              window.print();
            }
          <\/script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    } else {
      window.print();
    }
  };

  const handleDownloadPDF = async () => {
    if (contentRef.current) {
      try {
        const canvas = await html2canvas(contentRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: true,
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        const refNo = booking?.invoiceNo || booking?.pnr || booking?.bookingNo || booking?.busBookingId || Date.now();
        pdf.save(`Agent_Invoice_${refNo}.pdf`);
      } catch (err) {
        alert("Download failed. Please try printing the ticket instead.");
        console.error("PDF download error:", err);
      }
    }
  };

  if (loading) {
    return (
      <div id="layout-wrapper">
        <Header User={user} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div className="spinner-border text-secondary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5>Loading Invoice Details...</h5>
                      <p className="text-muted">Please wait while we fetch your booking information</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div id="layout-wrapper">
        <Header User={user} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div className="mb-4">
                        <RiCheckboxCircleLine className="text-secondary fs-1" />
                      </div>
                      <h4 className="mb-3">{error || "Invoice not found"}</h4>
                      <p className="text-muted mb-4">
                        The invoice you're looking for doesn't exist or you don't have permission to view it.
                      </p>
                      <button onClick={handleBack} className="btn btn-secondary">
                        <RiArrowLeftLine className="me-2" />
                        Back to Bookings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const paymentSummary = getPaymentSummary();

  return (
    <div id="layout-wrapper">
      <Header User={user} />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Back Button & Action Buttons - Top */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 d-print-none">
                  <button onClick={handleBack} className="btn btn-outline-secondary d-flex align-items-center gap-2">
                    <RiArrowLeftLine /> Back to Bookings
                  </button>
                  <div className="d-flex gap-2">
                    <button className="btn btn-secondary d-flex align-items-center gap-2" onClick={handlePrint}>
                      <RiPrinterLine /> Print Invoice
                    </button>
                    <button className="btn btn-secondary d-flex align-items-center gap-2" onClick={handleDownloadPDF}>
                      <RiDownloadLine /> Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Content */}
            <div ref={contentRef}>
              <div ref={printRef} className="row justify-content-center">
                <div className="col-lg-10">
                  <div className="card" style={{ border: '2px solid #000000' }}>
                    {/* Invoice Header */}
                    <div className="card-body" style={{ borderBottom: '3px solid #000000', paddingBottom: '20px' }}>
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex align-items-center gap-3">
                            <div className="rounded-circle" style={{ border: '2px solid #000000', padding: '10px' }}>
                              <RiBillLine className="fs-2" />
                            </div>
                            <div>
                              <h3 className="mb-1" style={{ fontWeight: "bold" }}>
                                AGENT INVOICE
                              </h3>
                              <h5 className="mb-1">{companyInfo.name}</h5>
                              <p className="mb-0 small" style={{ color: "#666666" }}>
                                {companyInfo.email}
                                {companyInfo.phone && ` | ${companyInfo.phone}`}
                              </p>
                              {companyInfo.gst && (
                                <p className="mb-0 small" style={{ color: "#666666" }}>
                                  GSTIN: {companyInfo.gst}
                                </p>
                              )}
                              {companyInfo.address && (
                                <p className="mb-0 small" style={{ color: "#666666" }}>
                                  {companyInfo.address}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 text-end">
                          <div style={{ border: '1px solid #000000', padding: '8px 12px' }}>
                            <small className="d-block" style={{ color: '#666666' }}>Invoice No.</small>
                            <strong style={{ fontSize: '18px' }}>
                              {booking.invoiceNo || booking.bookingNo || "N/A"}
                            </strong>
                          </div>
                          <div style={{ marginTop: '8px' }}>
                            <small style={{ color: '#666666' }}>Invoice Date: {formatDate(booking.bookingDate)}</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-body">
                      {/* Journey Details */}
                      <div className="row mb-4">
                        <div className="col-12">
                          <div style={{ background: '#f8f9fa', padding: '15px', border: '1px solid #000000' }}>
                            <div className="row align-items-center">
                              <div className="col-4 text-center">
                                <h4 style={{ fontWeight: 'bold' }}>{booking.sourceCity}</h4>
                                <p style={{ color: '#666666', marginBottom: '0' }}>
                                  <RiTimeLine className="me-1" />
                                  {boardingTime || "Time not available"}
                                </p>
                                <small style={{ color: '#666666' }}>
                                  <RiMapPinLine className="me-1" />
                                  {boardingPointDisplay.split(',')[0] || "Boarding Point"}
                                </small>
                              </div>
                              <div className="col-4 text-center">
                                <RiArrowLeftRightLine className="fs-1" />
                                <div className="rounded-pill px-3 py-1 small d-inline-block" style={{ border: '1px solid #000000' }}>
                                  {booking.journeyType || "ONE_WAY"}
                                </div>
                                <div className="small" style={{ color: '#666666', marginTop: '4px' }}>
                                  {formatDate(booking.departureDate)}
                                </div>
                              </div>
                              <div className="col-4 text-center">
                                <h4 style={{ fontWeight: 'bold' }}>{booking.destinationCity}</h4>
                                <p style={{ color: '#666666', marginBottom: '0' }}>
                                  <RiTimeLine className="me-1" />
                                  {droppingTime || "Time not available"}
                                </p>
                                <small style={{ color: '#666666' }}>
                                  <RiMapPinLine className="me-1" />
                                  {droppingPointDisplay.split(',')[0] || "Dropping Point"}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bus Details and Booking Details */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div style={{ border: '1px solid #000000', padding: '15px' }}>
                            <h6 className="section-title" style={{ borderBottom: '2px solid #000000', paddingBottom: '8px' }}>
                              <RiBusLine className="me-2" />
                              BUS DETAILS
                            </h6>
                            <div className="d-flex justify-content-between mb-2">
                              <span style={{ color: '#666666' }}>Operator</span>
                              <strong>{booking.operatorName || "N/A"}</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span style={{ color: '#666666' }}>Bus Type</span>
                              <strong>{booking.busType || "N/A"}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span style={{ color: '#666666' }}>Journey Type</span>
                              <strong>{booking.journeyType || "ONE_WAY"}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ border: '1px solid #000000', padding: '15px' }}>
                            <h6 className="section-title" style={{ borderBottom: '2px solid #000000', paddingBottom: '8px' }}>
                              <RiFileList3Line className="me-2" />
                              BOOKING DETAILS
                            </h6>
                            <div className="d-flex justify-content-between mb-2">
                              <span style={{ color: '#666666' }}>PNR</span>
                              <strong>{booking.pnr || "N/A"}</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span style={{ color: '#666666' }}>Booking Status</span>
                              <strong>{booking.bookingStatus || "N/A"}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span style={{ color: '#666666' }}>Payment Status</span>
                              <strong>{booking.paymentStatus || "PENDING"}</strong>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Boarding & Dropping Points */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div style={{ borderLeft: '3px solid #000000', paddingLeft: '15px' }}>
                            <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>BOARDING POINT</h6>
                            <p className="mb-1">{boardingPointDisplay}</p>
                            <p style={{ color: '#666666', fontSize: '12px', marginBottom: '0' }}>
                              <RiTimeLine className="me-1" />
                              Time: {boardingTime}
                            </p>
                            <p style={{ color: '#666666', fontSize: '12px', marginBottom: '0' }}>Report 30 minutes before departure</p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ borderLeft: '3px solid #000000', paddingLeft: '15px' }}>
                            <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>DROPPING POINT</h6>
                            <p className="mb-1">{droppingPointDisplay}</p>
                            <p style={{ color: '#666666', fontSize: '12px', marginBottom: '0' }}>
                              <RiTimeLine className="me-1" />
                              Time: {droppingTime}
                            </p>
                            <p style={{ color: '#666666', fontSize: '12px', marginBottom: '0' }}>Estimated arrival time</p>
                          </div>
                        </div>
                      </div>

                      {/* Passenger Details */}
                      <div className="card" style={{ border: '1px solid #000000', marginBottom: '20px' }}>
                        <div className="card-body" style={{ padding: '15px' }}>
                          <h5 className="section-title" style={{ borderBottom: '2px solid #000000', paddingBottom: '8px', marginBottom: '15px' }}>
                            <RiUserLine className="me-2" />
                            PASSENGER DETAILS
                          </h5>
                          <div className="table-responsive">
                            <table className="table" style={{ border: '1px solid #000000' }}>
                              <thead style={{ background: '#f8f9fa' }}>
                                <tr>
                                  <th style={{ border: '1px solid #000000' }}>#</th>
                                  <th style={{ border: '1px solid #000000' }}>Passenger Name</th>
                                  <th style={{ border: '1px solid #000000' }}>Seat</th>
                                  <th style={{ border: '1px solid #000000' }}>Age</th>
                                  <th style={{ border: '1px solid #000000' }}>Gender</th>
                                  <th style={{ border: '1px solid #000000' }}>ID Proof</th>
                                </tr>
                              </thead>
                              <tbody>
                                {passengerData.length > 0 ? (
                                  passengerData.map((passenger, index) => {
                                    let seatNumber = "N/A";
                                    if (passenger) {
                                      if (passenger.SeatNbr) seatNumber = passenger.SeatNbr;
                                      else if (passenger.seatNumber) seatNumber = passenger.seatNumber;
                                      else if (passenger.SeatNo) seatNumber = passenger.SeatNo;
                                      else if (passenger.seatNo) seatNumber = passenger.seatNo;
                                      else if (passenger.seat) seatNumber = passenger.seat;
                                    }

                                    return (
                                      <tr key={index}>
                                        <td style={{ border: '1px solid #000000' }}>{index + 1}</td>
                                        <td style={{ border: '1px solid #000000' }}>
                                          <strong>{passenger.Name}</strong>
                                          <div className="small" style={{ color: '#666666' }}>{passenger.Mobile}</div>
                                        </td>
                                        <td style={{ border: '1px solid #000000' }}>{seatNumber}</td>
                                        <td style={{ border: '1px solid #000000' }}>{passenger.Age || "N/A"}</td>
                                        <td style={{ border: '1px solid #000000' }}>{passenger.Sex || "N/A"}</td>
                                        <td style={{ border: '1px solid #000000' }}>{passenger.IdType || "N/A"}</td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td style={{ border: '1px solid #000000' }}>1</td>
                                    <td style={{ border: '1px solid #000000' }}>
                                      <strong>{booking.passengerName || "N/A"}</strong>
                                    </td>
                                    <td style={{ border: '1px solid #000000' }}>{booking.seatNumber || booking.seatNo || "N/A"}</td>
                                    <td style={{ border: '1px solid #000000' }}>N/A</td>
                                    <td style={{ border: '1px solid #000000' }}>N/A</td>
                                    <td style={{ border: '1px solid #000000' }}>N/A</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* Payment Summary - Agent Style */}
                      <div className="row mt-3">
                        <div className="col-md-6 offset-md-6">
                          <div className="card border-0 shadow-sm" style={{ border: '1px solid #000000' }}>
                            <div className="card-header bg-white border-bottom-0" style={{ padding: '15px 15px 0 15px' }}>
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <RiMoneyDollarCircleLine className="text-muted" />
                                Fare Summary
                              </h5>
                            </div>
                            <div className="card-body" style={{ padding: '15px' }}>
                              <div className="fare-summary">
                                {/* Bus Fare */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div>
                                    <span className="text-muted">Bus Fare</span>
                                  </div>
                                  <span className="fw-semibold">
                                    {formatCurrency(paymentSummary.baseFare)}
                                  </span>
                                </div>

                            
                               
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                      <span className="text-muted">Platform Fee</span>
                                      <div className="small text-muted">
                                        include 18% GST
                                      </div>
                                    </div>
                                    <span className="fw-semibold">
                                      {formatCurrency(paymentSummary.platformFee)}
                                    </span>
                                  </div>
                                
                                  {/* {paymentSummary.convenienceFee > 0 && (
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                      <span className="text-muted">Convenience Fee</span>
                                    </div>
                                    <span className="fw-semibold">
                                      {formatCurrency(paymentSummary.convenienceFee)}
                                    </span>
                                  </div>
                                )} */}
                                {/* Payment Gateway Fee - ONLY show if greater than 0 */}
                                {paymentSummary.paymentGatewayAmount > 0 && (
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                      <span className="text-muted">Payment Gateway Fee</span>
                                    </div>
                                    <span className="fw-semibold">
                                      {formatCurrency(Math.round(paymentSummary.paymentGatewayAmount || 0))}
                                    </span>
                                  </div>
                                )}

                                {/* Discount */}
                                {paymentSummary.discount > 0 && (
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                      <span className="text-muted">Discount</span>
                                    </div>
                                    <span className="fw-semibold">
                                      -{formatCurrency(paymentSummary.discount)}
                                    </span>
                                  </div>
                                )}

                                <hr className="my-3" />

                                {/* Total Amount */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <strong className="fs-5">Total Amount</strong>
                                  <strong className="fw-semibold">
                                    {formatCurrency(paymentSummary.totalAmount)}
                                  </strong>
                                </div>

                                
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Important Guidelines */}
                      <div className="row mt-4">
                        <div className="col-12">
                          <div style={{ border: '1px solid #000000', padding: '12px 15px', background: '#f8f9fa' }}>
                            <h6 className="mb-2 d-flex align-items-center gap-2" style={{ fontWeight: 'bold' }}>
                              <RiErrorWarningLine />
                              IMPORTANT GUIDELINES
                            </h6>
                            <ul className="list-unstyled small mb-0">
                              <li className="mb-1">✓ Arrive at boarding point 30 minutes before departure</li>
                              <li className="mb-1">✓ Carry valid government-issued photo ID proof</li>
                              <li className="mb-1">✓ Keep this invoice handy for verification</li>
                              <li className="mb-0">✓ Contact operator for journey-related queries</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="row mt-4">
                        <div className="col-12">
                          <hr style={{ borderTop: '1px solid #000000' }} />
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small style={{ color: '#666666' }}>
                                <RiShieldCheckLine className="me-1" />
                                This is a system generated invoice
                              </small>
                            </div>
                            <div className="text-end">
                              <small style={{ color: '#666666' }}>
                                Generated on: {formatDate(new Date())}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusAgentInvoice;